import { useEffect, useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTutorialStore } from '../../store/tutorialStore';
import { TUTORIAL_STEPS, TOTAL_STEPS } from './tutorialSteps';
import { useSpotlightPosition } from '../../hooks/useSpotlightPosition';
import SpotlightOverlay from './SpotlightOverlay';
import { TutorialTooltip } from './TutorialTooltip';
import { useToast } from '../Toast/ToastProvider';

// Step indices — update if TUTORIAL_STEPS order changes
const STEP_CREATE_TASK = 1;
const STEP_ADD_SUBTASK = 3;
const STEP_DRAG_CHIP = 4;

export function TutorialController() {
  const { isActive, currentStep, nextStep, prevStep, skipTutorial, advanceIfStep } = useTutorialStore();
  const { addToast } = useToast();
  const [showSuccess, setShowSuccess] = useState(false);

  const step = TUTORIAL_STEPS[currentStep];
  const rect = useSpotlightPosition(isActive ? (step?.target ?? null) : null);

  const handleSkip = useCallback(() => {
    addToast("Tutorial skipped. Press ⌘K and search 'Replay tutorial' anytime.", 'reminder');
    skipTutorial();
  }, [addToast, skipTutorial]);

  const handleNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const handleBack = useCallback(() => {
    prevStep();
  }, [prevStep]);

  // Escape key to skip
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleSkip();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isActive, handleSkip]);

  // Listen for interactive step events
  useEffect(() => {
    if (!isActive) return;

    const handleTaskCreated = () => {
      if (useTutorialStore.getState().currentStep !== STEP_CREATE_TASK) return;
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        advanceIfStep(STEP_CREATE_TASK);
      }, 800);
    };

    const handleSubtaskCreated = () => {
      if (useTutorialStore.getState().currentStep !== STEP_ADD_SUBTASK) return;
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        advanceIfStep(STEP_ADD_SUBTASK);
      }, 800);
    };

    const handleDragComplete = (e: Event) => {
      if (useTutorialStore.getState().currentStep !== STEP_DRAG_CHIP) return;
      const detail = (e as CustomEvent).detail;
      if (detail?.progress >= 40) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          advanceIfStep(STEP_DRAG_CHIP);
        }, 800);
      }
    };

    window.addEventListener('tutorial:task-created', handleTaskCreated);
    window.addEventListener('tutorial:subtask-created', handleSubtaskCreated);
    window.addEventListener('tutorial:drag-complete', handleDragComplete);
    return () => {
      window.removeEventListener('tutorial:task-created', handleTaskCreated);
      window.removeEventListener('tutorial:subtask-created', handleSubtaskCreated);
      window.removeEventListener('tutorial:drag-complete', handleDragComplete);
    };
  }, [isActive, advanceIfStep]);

  // Auto-open QuickAdd when on create-task step
  useEffect(() => {
    if (!isActive || currentStep !== STEP_CREATE_TASK) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tutorial:open-quick-add'));
    }, 300);
    return () => clearTimeout(timer);
  }, [isActive, currentStep]);

  // Auto-open inline add-subtask form when on add-subtask step
  useEffect(() => {
    if (!isActive || currentStep !== STEP_ADD_SUBTASK) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('tutorial:open-add-subtask'));
    }, 400);
    return () => clearTimeout(timer);
  }, [isActive, currentStep]);

  if (!isActive || !step) return null;

  return (
    <AnimatePresence>
      <SpotlightOverlay rect={rect} />
      <TutorialTooltip
        step={step}
        rect={rect}
        currentStepIndex={currentStep}
        totalSteps={TOTAL_STEPS}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
        showSuccess={showSuccess}
      />
    </AnimatePresence>
  );
}
